/**
 * Currency utility functions for proposal components
 */

/**
 * Get currency symbol for a given currency code
 * @param {string} currencyCode - The currency code (USD, EUR, GBP, etc.)
 * @returns {string} The currency symbol
 */
export const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode) {
        case 'USD': return '$';
        case 'EUR': return '€';
        case 'GBP': return '£';
        case 'JPY': return '¥';
        case 'SGD': return 'S$';
        case 'AUD': return 'A$';
        case 'IDR': return 'Rp';
        default: return 'Rp';
    }
};

/**
 * Get currency prefix (symbol + space) for a given currency code
 * @param {string} currencyCode - The currency code
 * @returns {string} The currency prefix with space
 */
export const getCurrencyPrefix = (currencyCode) => {
    return getCurrencySymbol(currencyCode) + ' ';
};

/**
 * Format currency amount with proper symbol and locale formatting
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - The currency code
 * @param {string} locale - The locale for formatting (default: 'id-ID')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currencyCode = 'IDR', locale = 'id-ID') => {
    const symbol = getCurrencySymbol(currencyCode);
    const formattedAmount = amount ? amount.toLocaleString(locale) : '0';
    return `${symbol} ${formattedAmount}`;
};

/**
 * Parse currency string to number
 * @param {string} currencyString - The currency string to parse
 * @returns {number} The parsed number
 */
export const parseCurrency = (currencyString) => {
    if (!currencyString) return 0;
    // Remove currency symbols and spaces, then parse
    const cleanString = currencyString.replace(/[^\d.,]/g, '');
    return parseFloat(cleanString.replace(',', '.')) || 0;
};

/**
 * Get default currency code based on application settings
 * @returns {string} Default currency code
 */
export const getDefaultCurrency = () => {
    return 'IDR';
};

/**
 * Validate currency code
 * @param {string} currencyCode - The currency code to validate
 * @returns {boolean} True if valid currency code
 */
export const isValidCurrencyCode = (currencyCode) => {
    const validCodes = ['USD', 'EUR', 'GBP', 'JPY', 'SGD', 'AUD', 'IDR'];
    return validCodes.includes(currencyCode);
};
